import { useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300

export type ReviewQueryOpts<TSort extends string> = {
  periodId?: number
  reviewerId?: number
  search?: string
  reviewerName?: string
  sort?: TSort
}

type PagedResult<TRow> = {
  content: TRow[]
  totalElements: number
}

type UseReviewsQueryArgs<TRow, TSort extends string> = {
  refreshKey: number
  fetchPage: (page: number, size: number, opts: ReviewQueryOpts<TSort>) => Promise<PagedResult<TRow>>
  errorMessage: string
  periodId?: number
  reviewerId?: number
  initialSearch?: string
  initialReviewerName?: string
  sort?: TSort
  initialPage?: number
}

type UseReviewsQuery<TRow> = {
  rows: TRow[]
  totalElements: number
  loading: boolean
  error: string | null
  page: number
  pageSize: number
  search: string
  reviewerName: string
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  onSearchChange: (value: string) => void
  onReviewerNameChange: (value: string) => void
}

type QueryState = {
  page: number
  search: string
  reviewerName: string
}

const useReviewsQuery = <TRow, TSort extends string>(args: UseReviewsQueryArgs<TRow, TSort>): UseReviewsQuery<TRow> => {
  const {
    refreshKey,
    fetchPage,
    errorMessage,
    periodId,
    reviewerId,
    initialSearch = '',
    initialReviewerName = '',
    sort,
    initialPage = 0,
  } = args

  const [data, setData] = useState<PagedResult<TRow> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState(initialSearch)
  const [reviewerName, setReviewerName] = useState(initialReviewerName)
  const [query, setQuery] = useState<QueryState>({
    page: initialPage,
    search: initialSearch,
    reviewerName: initialReviewerName,
  })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset to page 0 when period/reviewer/sort change — done during render
  // so the fetch effect below sees page=0 on the same render and we avoid
  // a wasted request with the stale page.
  const filterKey = `${periodId ?? ''}|${reviewerId ?? ''}|${sort ?? ''}`
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    setQuery(q => (q.page === 0 ? q : { ...q, page: 0 }))
  }

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchPage(query.page, pageSize, {
      periodId,
      reviewerId,
      search: query.search,
      reviewerName: query.reviewerName,
      sort,
    })
      .then(setData)
      .catch(() => setError(errorMessage))
      .finally(() => setLoading(false))
  }, [query, pageSize, periodId, reviewerId, refreshKey, sort, fetchPage, errorMessage])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const scheduleQueryPatch = useCallback((patch: Partial<QueryState>) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setQuery(q => ({ ...q, page: 0, ...patch }))
    }, SEARCH_DEBOUNCE_MS)
  }, [])

  const onSearchChange = useCallback(
    (value: string) => {
      setSearch(value)
      scheduleQueryPatch({ search: value })
    },
    [scheduleQueryPatch]
  )

  const onReviewerNameChange = useCallback(
    (value: string) => {
      setReviewerName(value)
      scheduleQueryPatch({ reviewerName: value })
    },
    [scheduleQueryPatch]
  )

  const onPageChange = useCallback((page: number) => {
    setQuery(q => ({ ...q, page }))
  }, [])

  const onPageSizeChange = useCallback((size: number) => {
    setPageSize(size)
    setQuery(q => ({ ...q, page: 0 }))
  }, [])

  return {
    rows: data?.content ?? [],
    totalElements: data?.totalElements ?? 0,
    loading,
    error,
    page: query.page,
    pageSize,
    search,
    reviewerName,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onReviewerNameChange,
  }
}

export default useReviewsQuery
